import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import RowsIndicator from '../common/RowsIndicator';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { useNavigate } from 'react-router-dom';
import { FiFilter, FiColumns, FiDownload, FiX } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import { formatUSDate } from '../Scheduling';

// Import shared components
import { ColumnVisibilityDropdown, TooltipCell, ResetButton, SortIcon, sortIconDirection } from '../common';
import { formatCurrency, formatDate } from '../../utils';
import { loadXLSX } from '../../utils/xlsxLoader';
import { openProjectFromOpportunity } from '../../utils/projectNavigation';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useUserPreference } from '../../hooks/useUserPreference';
import { useFilterPopupPortal } from '../../hooks/useFilterPopupPortal';
import { USER_PREFERENCE_KEYS } from '../../constants/userPreferences';

const DEFAULT_COLUMN_ORDER = [
  'Project__r.Project_System_Wide_ID__c',
  'Project__r.Name',
  'dv_Division_New__c',
  'dv_Type__c',
  'Contract_With__r.Name',
  'RevisedRevenueBudget',
  'Company__c',
  'manager',
  'Status',
  'BillingProgress',
  'next_onsite',
  'first_onsite',
  'most_recent_onsite',
  'created_at',
  'project_info.bill_date',
  'project_info.contract',
  'project_info.tax_exempt',
  'project_info.certified_payroll',
  'project_info.p_and_p_bonds',
  'project_info.ccip',
  'project_info.retainage',
  'project_info.prevailing_wage',
  'Project__r.Street__c',
  'Project__r.gmb_City__c',
  'Project__r.gmb_Project_State__c',
  'Project__r.Zip_Code__c'
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) {
    return DEFAULT_COLUMN_ORDER;
  }

  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) {
      merged.push(key);
    }
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const DEFAULT_VISIBLE_COLUMNS = [
  'Project__r.Project_System_Wide_ID__c',
  'Project__r.Name',
  'dv_Division_New__c',
  'dv_Type__c',
  'Contract_With__r.Name',
];

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  return savedColumns.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) {
    return DEFAULT_PINNED_COLUMNS;
  }
  return savedPins.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

// Add this constant back
const ITEMS_PER_PAGE = 15;
const INITIAL_LOAD_COUNT = 15;

// Add display label mappings at the top of the file
const SCOPE_LABELS = {
  'Smoke Curtains': 'Smoke Curtains',
  'Tectum': 'Tectum',
  'Gyp': 'Gyp',
  'Floors': 'Floors',
  'Roof Deck': 'Roof Deck',
  // Add more as needed
};
const DIVISION_LABELS = {
  'Doors': 'Doors',
  'Roof Deck': 'Roof Deck',
  'Floors': 'Floors',
  // Add more as needed
};
const STATUS_LABELS = {
  'Active': 'Active',
  'In Planning': 'In Planning',
  // Add more as needed
};

// TooltipCell is now imported from '../common'

function FilterCloseButton({ onClose }) {
  return (
    <button
      type="button"
      aria-label="Close filter"
      onClick={onClose}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
    >
      <FiX className="h-4 w-4" strokeWidth={2} />
    </button>
  );
}

function FilterTitleHeader({ title, onClose }) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white px-3 py-2">
      <div className="text-sm font-medium text-gray-700">{title}</div>
      <FilterCloseButton onClose={onClose} />
    </div>
  );
}

function FilterSearchHeader({ searchTerm, onSearchChange, onClose }) {
  return (
    <div className="sticky top-0 z-10 bg-white p-2 border-b">
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          placeholder="Search..."
          className="h-8 flex-1 min-w-0 rounded border border-gray-300 px-2.5 text-sm text-gray-900 font-normal focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <FilterCloseButton onClose={onClose} />
      </div>
    </div>
  );
}

// Update the ColumnFilterDropdown component
const ColumnFilterDropdown = ({ isOpen, options, selectedValues, onSelect, onClose, maxHeight = '60vh' }) => {
  const [tempSelected, setTempSelected] = useState([...selectedValues]);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const [effectiveMaxHeight, setEffectiveMaxHeight] = useState(maxHeight);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // Reset search term and temp selected when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTempSelected([...selectedValues]);
    }
  }, [isOpen, selectedValues]);

  // UX: Do not auto-close on scroll/outside taps; only close via Clear/Apply
  // (or by tapping the same filter button again).

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recalculate = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const scrollEl = dropdown.querySelector('.filter-options-scroll');
      if (!scrollEl) return;

      const margin = 8;
      const viewportW = window.innerWidth || document.documentElement.clientWidth;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;

      const dropdownRect = dropdown.getBoundingClientRect();

      let x = 0;
      if (dropdownRect.left < margin) x = margin - dropdownRect.left;
      if (dropdownRect.right > viewportW - margin) x = (viewportW - margin) - dropdownRect.right;

      let y = 0;
      if (dropdownRect.top < margin) y = margin - dropdownRect.top;
      if (dropdownRect.bottom > viewportH - margin) y = (viewportH - margin) - dropdownRect.bottom;

      setOffsetX(x);
      setOffsetY(y);

      // Tighten options scroll height if the dropdown is pushed below the viewport.
      if (dropdownRect.bottom > viewportH - margin) {
        const overflowY = dropdownRect.bottom - (viewportH - margin);
        const scrollRect = scrollEl.getBoundingClientRect();
        const newScrollHeight = Math.max(120, scrollRect.height - overflowY);
        setEffectiveMaxHeight(`${newScrollHeight}px`);
      } else {
        setEffectiveMaxHeight(maxHeight);
      }
    };

    recalculate();

    window.addEventListener('resize', recalculate);
    window.addEventListener('orientationchange', recalculate);

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('orientationchange', recalculate);
    };
  }, [isOpen, maxHeight]);

  if (!isOpen) return null;

  // Helper function to get the actual value from an option (handles both objects and primitives)
  const getOptionValue = (option) => {
    return option && typeof option === 'object' && option.value !== undefined ? option.value : option;
  };

  // Helper function to get the display label from an option
  const getOptionLabel = (option) => {
    return option && typeof option === 'object' && option.label !== undefined ? option.label : option;
  };

  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option);
    return label.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle temporary selection
  const handleTempSelect = (value) => {
    const actualValue = getOptionValue(value);
    setTempSelected(prev => {
      if (prev.includes(actualValue)) {
        return prev.filter(v => v !== actualValue);
      }
      return [...prev, actualValue];
    });
  };

  // Handle Select All
  const handleSelectAll = () => {
    if (tempSelected.length === filteredOptions.length) {
      setTempSelected([]);
    } else {
      const allValues = filteredOptions.map(option => getOptionValue(option));
      setTempSelected(allValues);
    }
  };

  // Handle Apply
  const handleApply = () => {
    // Clear existing selections first
    selectedValues.forEach(value => {
      if (!tempSelected.includes(value)) {
        onSelect(value); // This toggles off the values that were selected before but aren't now
      }
    });

    // Then add new selections
    tempSelected.forEach(value => {
      if (!selectedValues.includes(value)) {
        onSelect(value); // This toggles on the values that weren't selected before but are now
      }
    });

    onClose();
  };

  // Handle Clear
  const handleClear = () => {
    // Clear the temporary selection state
    setTempSelected([]);
    // Clear search term
    setSearchTerm('');
    // Clear all selections in parent component
    selectedValues.forEach(value => {
      onSelect(value);
    });
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className="projects-filter-popup z-50 bg-white border rounded-md shadow-lg w-full sm:w-80"
    >
      <div className="flex flex-col">
        <FilterSearchHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClose={onClose}
        />

        {/* Select/Deselect All */}
        <div className="sticky top-[49px] bg-white p-2 border-b">
          <label className="flex items-center gap-2 p-1">
            <input
              type="checkbox"
              checked={tempSelected.length === filteredOptions.length && filteredOptions.length > 0}
              onChange={handleSelectAll}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-900 font-normal">Select All</span>
          </label>
        </div>

        {/* Options list with height of ~60% viewport on mobile then scroll */}
        <div
          className="p-2 pb-16 filter-options-scroll"
          style={{
            maxHeight: effectiveMaxHeight,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
          onScroll={(e) => e.stopPropagation()}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              const isSelected = tempSelected.includes(optionValue);

              return (
                <label
                  key={index}
                  className="flex items-center gap-2 p-1 hover:bg-gray-50"
                  style={{ height: '24px' }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleTempSelect(option)}
                    className={`rounded ${isSelected ? 'border-red-500 text-red-600' : 'border-gray-300'}`}
                  />
                  <span
                    className={`text-sm ${isSelected ? 'text-red-600 font-medium' : 'text-gray-900 font-normal'} truncate flex-1 text-left`}
                    title={optionLabel.length > 30 ? optionLabel : ''}
                  >
                    {optionLabel.length > 30 ? `${optionLabel.substring(0, 30)}...` : optionLabel}
                  </span>
                </label>
              );
            })
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No matching options found
            </div>
          )}
        </div>

        {/* Clear and Apply buttons */}
        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Filter Icon component
const FilterIcon = ({ active, onClick }) => (
  <button
    onClick={onClick}
    className={`p-1 hover:bg-gray-100 rounded ${active ? 'text-red-600' : 'text-gray-400'}`}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  </button>
);

// Update the RangeFilterDropdown component
const RangeFilterDropdown = ({ isOpen, onApply, onClose, currentRange }) => {
  const [minValue, setMinValue] = useState(currentRange?.min || '');
  const [maxValue, setMaxValue] = useState(currentRange?.max || '');
  const dropdownRef = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // UX: Do not auto-close on outside taps / scrolling.

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recalculate = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const margin = 8;
      const viewportW = window.innerWidth || document.documentElement.clientWidth;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const dropdownRect = dropdown.getBoundingClientRect();

      let x = 0;
      if (dropdownRect.left < margin) x = margin - dropdownRect.left;
      if (dropdownRect.right > viewportW - margin) x = (viewportW - margin) - dropdownRect.right;

      let y = 0;
      if (dropdownRect.top < margin) y = margin - dropdownRect.top;
      if (dropdownRect.bottom > viewportH - margin) y = (viewportH - margin) - dropdownRect.bottom;

      setOffsetX(x);
      setOffsetY(y);
    };

    recalculate();
    window.addEventListener('resize', recalculate);
    window.addEventListener('orientationchange', recalculate);

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('orientationchange', recalculate);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    const min = minValue === '' ? null : Number(minValue);
    const max = maxValue === '' ? null : Number(maxValue);
    onApply({ min, max });
    onClose();
  };

  const handleClear = () => {
    // Clear the current values
    setMinValue('');
    setMaxValue('');
    // Apply the cleared values
    onApply({ min: null, max: null });
    onClose();
  };

  const formatValue = (value) => {
    if (value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div
      ref={dropdownRef}
      className="projects-filter-popup z-50 bg-white border rounded-md shadow-lg w-full sm:w-80 max-w-[90vw] sm:max-w-none"
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <div className="flex flex-col">
        <FilterTitleHeader title="Filter by Amount Range" onClose={onClose} />
        <div className="p-4 pt-3">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Minimum Value</label>
              <input
                type="number"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                placeholder="Min Amount"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
              {minValue && (
                <div className="text-xs text-gray-500 mt-1">
                  {formatValue(minValue)}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Maximum Value</label>
              <input
                type="number"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                placeholder="Max Amount"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
              {maxValue && (
                <div className="text-xs text-gray-500 mt-1">
                  {formatValue(maxValue)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update the formatDateForDisplay function
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Update the DateRangeFilterDropdown component
const DateRangeFilterDropdown = ({ isOpen, onApply, onClose, currentRange }) => {
  const [startDate, setStartDate] = useState(currentRange?.start || '');
  const [endDate, setEndDate] = useState(currentRange?.end || '');
  const dropdownRef = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // UX: Do not auto-close on outside tap / scroll.

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recalculate = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const margin = 8;
      const viewportW = window.innerWidth || document.documentElement.clientWidth;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const dropdownRect = dropdown.getBoundingClientRect();

      let x = 0;
      if (dropdownRect.left < margin) x = margin - dropdownRect.left;
      if (dropdownRect.right > viewportW - margin) x = (viewportW - margin) - dropdownRect.right;

      let y = 0;
      if (dropdownRect.top < margin) y = margin - dropdownRect.top;
      if (dropdownRect.bottom > viewportH - margin) y = (viewportH - margin) - dropdownRect.bottom;

      setOffsetX(x);
      setOffsetY(y);
    };

    recalculate();
    window.addEventListener('resize', recalculate);
    window.addEventListener('orientationchange', recalculate);

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('orientationchange', recalculate);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ start: startDate, end: endDate });
    onClose();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    onApply({ start: null, end: null });
    onClose();
  };

  // Helper function to format date as MM-DD-YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Helper function to convert date to yyyy-mm-dd format for input
  const formatForDateInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // Helper function to handle date change
  const handleDateChange = (value, setDate) => {
    if (!value) {
      setDate('');
      return;
    }
    setDate(value);
  };

  return (
    <div
      ref={dropdownRef}
      className="projects-filter-popup z-50 bg-white border rounded-md shadow-lg w-full sm:w-80 max-w-[90vw] sm:max-w-none"
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <div className="flex flex-col">
        <FilterTitleHeader title="Filter by Date Range" onClose={onClose} />
        <div className="p-4 pt-3">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Start Date</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={startDate ? formatDateForDisplay(startDate) : ''}
                  placeholder="MM-DD-YYYY"
                  readOnly
                  className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500 cursor-pointer"
                />
                <input
                  type="date"
                  value={formatForDateInput(startDate)}
                  onChange={(e) => handleDateChange(e.target.value, setStartDate)}
                  className="absolute right-0 top-0 h-full px-2 py-1 opacity-0 cursor-pointer"
                  style={{ width: '2.5rem' }}
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-label="Calendar"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">End Date</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={endDate ? formatDateForDisplay(endDate) : ''}
                  placeholder="MM-DD-YYYY"
                  readOnly
                  className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500 cursor-pointer"
                />
                <input
                  type="date"
                  value={formatForDateInput(endDate)}
                  onChange={(e) => handleDateChange(e.target.value, setEndDate)}
                  className="absolute right-0 top-0 h-full px-2 py-1 opacity-0 cursor-pointer"
                  style={{ width: '2.5rem' }}
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-label="Calendar"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// formatDate is now imported from '../../utils'

// Create a combined filter dropdown for billing progress with improved interaction
const BillingProgressFilterDropdown = ({ isOpen, options, selectedValues, onSelect, onApply, onClose, currentRange }) => {
  const [tempSelected, setTempSelected] = useState([...selectedValues]);
  const [minValue, setMinValue] = useState(currentRange?.min || '');
  const [maxValue, setMaxValue] = useState(currentRange?.max || '');
  const [filterMode, setFilterMode] = useState(
    selectedValues.length > 0 ? 'checkbox' :
      (currentRange?.min !== null || currentRange?.max !== null) ? 'range' :
        'none'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 1024 : false));
  const dropdownRef = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset search term and temp selected when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTempSelected([...selectedValues]);
      setMinValue(currentRange?.min || '');
      setMaxValue(currentRange?.max || '');
      setFilterMode(
        selectedValues.length > 0 ? 'checkbox' :
          (currentRange?.min !== null || currentRange?.max !== null) ? 'range' :
            'none'
      );
    }
  }, [isOpen, selectedValues, currentRange]);

  // UX: Do not auto-close on outside taps / scrolling.

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recalculate = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const margin = 8;
      const viewportW = window.innerWidth || document.documentElement.clientWidth;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const dropdownRect = dropdown.getBoundingClientRect();

      let x = 0;
      if (dropdownRect.left < margin) x = margin - dropdownRect.left;
      if (dropdownRect.right > viewportW - margin) x = (viewportW - margin) - dropdownRect.right;

      let y = 0;
      if (dropdownRect.top < margin) y = margin - dropdownRect.top;
      if (dropdownRect.bottom > viewportH - margin) y = (viewportH - margin) - dropdownRect.bottom;

      setOffsetX(x);
      setOffsetY(y);
    };

    recalculate();
    window.addEventListener('resize', recalculate);
    window.addEventListener('orientationchange', recalculate);

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('orientationchange', recalculate);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Helper function to get the actual value from an option (handles both objects and primitives)
  const getOptionValue = (option) => {
    return option && typeof option === 'object' && option.value !== undefined ? option.value : option;
  };

  // Helper function to get the display label from an option
  const getOptionLabel = (option) => {
    return option && typeof option === 'object' && option.label !== undefined ? option.label : option;
  };

  // Handle temporary selection
  const handleTempSelect = (value) => {
    const actualValue = getOptionValue(value);
    setTempSelected(prev => {
      if (prev.includes(actualValue)) {
        return prev.filter(v => v !== actualValue);
      }
      return [...prev, actualValue];
    });
  };

  // Filter options based on search term first
  const searchFilteredOptions = options.filter(option => {
    const label = getOptionLabel(option);
    return label.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Then apply any range filters if needed
  const filteredOptions = searchFilteredOptions.filter(option => {
    // Apply range filter if in range mode
    if (filterMode === 'range') {
      const value = parseFloat(getOptionValue(option));
      const min = minValue === '' ? Number.MIN_SAFE_INTEGER : parseFloat(minValue);
      const max = maxValue === '' ? Number.MAX_SAFE_INTEGER : parseFloat(maxValue);

      return value >= min && value <= max;
    }

    return true;
  });

  // Handle Select All
  const handleSelectAll = () => {
    if (tempSelected.length === filteredOptions.length) {
      setTempSelected([]);
    } else {
      const allValues = filteredOptions.map(option => getOptionValue(option));
      setTempSelected(allValues);
    }
  };

  // Handle min/max input changes
  const handleMinChange = (e) => {
    const value = e.target.value;
    setMinValue(value);

    // Update filter mode but don't clear selections
    if (value !== '' || maxValue !== '') {
      setFilterMode('range');
    } else if (value === '' && maxValue === '') {
      setFilterMode('none');
    }
  };

  const handleMaxChange = (e) => {
    const value = e.target.value;
    setMaxValue(value);

    // Update filter mode but don't clear selections
    if (value !== '' || minValue !== '') {
      setFilterMode('range');
    } else if (value === '' && minValue === '') {
      setFilterMode('none');
    }
  };

  // Handle Apply
  const handleApply = () => {
    // Clear existing selections first
    selectedValues.forEach(value => {
      if (!tempSelected.includes(value)) {
        onSelect(value); // This toggles off the values that were selected before but aren't now
      }
    });

    // Then add new selections
    tempSelected.forEach(value => {
      if (!selectedValues.includes(value)) {
        onSelect(value); // This toggles on the values that weren't selected before but are now
      }
    });

    // Apply the min/max range
    const min = minValue === '' ? null : Number(minValue);
    const max = maxValue === '' ? null : Number(maxValue);
    onApply({ min, max });

    onClose();
  };

  // Handle Clear
  const handleClear = () => {
    // Clear everything
    setTempSelected([]);
    setMinValue('');
    setMaxValue('');
    setFilterMode('none');
    setSearchTerm('');

    // Clear all selections in parent component
    selectedValues.forEach(value => {
      onSelect(value);
    });

    // Clear min/max values
    onApply({ min: null, max: null });

    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className="projects-filter-popup z-50 bg-white border rounded-md shadow-lg w-auto min-w-[14rem] max-w-[90vw] sm:max-w-[18rem]"
    >
      <div className="flex flex-col">
        <FilterSearchHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClose={onClose}
        />

        {/* Min/Max Range Section */}
        <div className="p-2 border-b">
          <div className="text-sm font-medium text-gray-700 mb-2">Filter by Range</div>
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-1">Min %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={minValue}
                onChange={handleMinChange}
                placeholder="Min"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-1">Max %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={maxValue}
                onChange={handleMaxChange}
                placeholder="Max"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>

        {/* Select/Deselect All */}
        <div className="sticky top-[49px] bg-white p-2 border-b">
          <label className="flex items-center gap-2 p-1">
            <input
              type="checkbox"
              checked={tempSelected.length === filteredOptions.length && filteredOptions.length > 0}
              onChange={handleSelectAll}
              className="rounded border-gray-300"
              disabled={filteredOptions.length === 0}
            />
            <span className="text-sm text-gray-900 font-normal">
              Select All {filteredOptions.length > 0 ? `(${filteredOptions.length})` : ''}
            </span>
          </label>
        </div>

        {/* Options list with reduced height for compact view */}
        <div
          className="p-2 pb-16 filter-options-scroll"
          style={{
            maxHeight: isMobile ? '35vh' : '40vh',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
          onScroll={(e) => e.stopPropagation()}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              const isSelected = tempSelected.includes(optionValue);

              return (
                <label
                  key={index}
                  className="flex items-center gap-2 p-1 hover:bg-gray-50"
                  style={{ height: '24px' }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleTempSelect(option)}
                    className={`rounded ${isSelected ? 'border-red-500 text-red-600' : 'border-gray-300'}`}
                  />
                  <span
                    className={`text-sm ${isSelected ? 'text-red-600 font-medium' : 'text-gray-900 font-normal'} truncate flex-1 text-left`}
                    title={optionLabel.length > 30 ? optionLabel : ''}
                  >
                    {(optionLabel.length > 30 ? `${optionLabel.substring(0, 30)}...` : optionLabel)}%
                  </span>
                </label>
              );
            })
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No values in the selected range
            </div>
          )}
        </div>

        {/* Clear and Apply buttons */}
        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Robust date parsing for ISO and MM/DD/YYYY
function parseDateString(dateStr) {
  if (!dateStr) return new Date(0);
  // Try ISO first
  let d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  // Try MM/DD/YYYY
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    // MM/DD/YYYY
    const [mm, dd, yyyy] = parts;
    return new Date(`${yyyy}-${mm}-${dd}`);
  }
  return new Date(0);
}

// ColumnVisibilityDropdown is now imported from '../common'

export function ManageProject() {
  const { authFetch, isReady } = useAuthFetch();
  // Move all state declarations to the top
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 1024 : false));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [allProjects, setAllProjects] = useState(() => {
    const savedProjects = localStorage.getItem('projectsList');
    return savedProjects ? JSON.parse(savedProjects) : [];
  });

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentDisplayCount, setCurrentDisplayCount] = useState(INITIAL_LOAD_COUNT);
  const [searchTerm, setSearchTerm] = useState(() =>
    localStorage.getItem('searchTerm') || ''
  );
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem('sortConfig');
    return savedSort ? JSON.parse(savedSort) : { key: 'Name', direction: 'asc' };
  });
  const [selectedDepartment, setSelectedDepartment] = useState(() =>
    localStorage.getItem('selectedDepartment') || 'Engineered Fill'
  );
  const [selectedRowId, setSelectedRowId] = useState(() =>
    localStorage.getItem('selectedRowId') || null
  );
  const [pendingScrollToRowId, setPendingScrollToRowId] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  const tableScrollRef = useRef(null);
  // Proxy horizontal scrollbar (Tracking Details pattern): sits after the pinned
  // columns and is kept in sync with the table scroller, whose own horizontal
  // scrollbar is hidden.
  const hScrollbarRef = useRef(null);
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });

  // Add these state declarations before they're used
  const [columnFilterValues, setColumnFilterValues] = useState(() => {
    const saved = localStorage.getItem('columnFilterValues');
    const defaultValues = {
      division: [],
      scope: [],
      branch: [],
      projectStatus: [],
      projectManager: [],
      billingProgress: [],
      // Project Info filter values
      billDate: [],
      contract: [],
      taxExempt: [],
      certifiedPayroll: [],
      ppBonds: [],
      ccip: [],
      retainage: [],
      prevailingWage: [],
      // Project Address filter values
      street: [],
      city: [],
      state: [],
      zipCode: []
    };

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge with defaults to ensure all properties exist
        return { ...defaultValues, ...parsed };
      } catch (error) {
        console.error('Error parsing columnFilterValues from localStorage:', error);
        return defaultValues;
      }
    }
    return defaultValues;
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const savedFilters = localStorage.getItem('columnFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      'Project__r.Name': '',
      'Contract_With__r.Name': '',
      'dv_Division_New__c': '',
      'dv_Type__c': '',
      'Company__c': '',
      'manager': '',
      'Project__r.Project_Status__c': '',
      'Billing_Progress__c': '',
      'Next_Onsite_Date__c': '',
      // Project Info filters
      'project_info.bill_date': '',
      'project_info.contract': '',
      'project_info.tax_exempt': '',
      'project_info.certified_payroll': '',
      'project_info.p_and_p_bonds': '',
      'project_info.ccip': '',
      'project_info.retainage': '',
      'project_info.prevailing_wage': '',
      // Project Address filters
      'Project__r.Street__c': '',
      'Project__r.gmb_City__c': '',
      'Project__r.gmb_Project_State__c': '',
      'Project__r.Zip_Code__c': ''
    };
  });

  const [amountRange, setAmountRange] = useState(() => {
    const savedRange = localStorage.getItem('amountRange');
    return savedRange ? JSON.parse(savedRange) : { min: null, max: null };
  });

  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = localStorage.getItem('dateRange');
    return savedDateRange ? JSON.parse(savedDateRange) : { start: null, end: null };
  });

  // Add a new state for billing progress range
  const [billingProgressRange, setBillingProgressRange] = useState(() => {
    const saved = localStorage.getItem('billingProgressRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  // Add state for createdAt date range filter
  const [createdAtRange, setCreatedAtRange] = useState(() => {
    const saved = localStorage.getItem('createdAtRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });

  const [firstOnsiteRange, setFirstOnsiteRange] = useState(() => {
    const saved = localStorage.getItem('firstOnsiteRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });

  const [mostRecentOnsiteRange, setMostRecentOnsiteRange] = useState(() => {
    const saved = localStorage.getItem('mostRecentOnsiteRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });

  // Add this after other state declarations
  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.PROJECT_LIST_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'visibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.PROJECT_LIST_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.PROJECT_LIST_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'pinnedColumns',
    }
  );

  // Add this state for column visibility dropdown
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const columnSelectorRef = useRef(null);

  // Add state for dropdown position
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });

  // Add a ref for the Columns button
  const columnButtonRef = useRef(null);

  const navigate = useNavigate();

  // Fetch projects only if they're not already in state
  useEffect(() => {
    const fetchProjects = async () => {
      if (!isReady) {
        return;
      }

      setLoading(true);
      setFetchError(null);
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-opportunities`
        );
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody.message || `Request failed (${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data.records)) {
          throw new Error('Unexpected API response format');
        }
        const filteredProjects = data.records.filter(project => project.Project__r !== null);
        setAllProjects(filteredProjects);
        localStorage.setItem('projectsList', JSON.stringify(filteredProjects));

        // Always start from the initial page size on refresh.
        // Deep-link / return-to-row expansion is handled by pendingScrollToRowId.
        // Match list filtering (Material Sales are hidden from the table).
        const displayableCount = filteredProjects.filter(
          (project) => project.dv_Division_New__c !== 'Material Sales'
        ).length;
        setCurrentDisplayCount(INITIAL_LOAD_COUNT);
        setHasMoreData(displayableCount > INITIAL_LOAD_COUNT);
        localStorage.setItem('currentDisplayCount', String(INITIAL_LOAD_COUNT));
      } catch (error) {
        console.error('Error fetching projects:', error);
        setFetchError(error.message || 'Failed to load projects');
        setAllProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects(); // Always fetch fresh data on mount
  }, [authFetch, isReady]);

  useEffect(() => {
    const shouldScroll = localStorage.getItem('scrollToSelectedRow');
    if (shouldScroll === 'true') {
      localStorage.removeItem('scrollToSelectedRow');
      const storedRowId = localStorage.getItem('selectedRowId');
      if (storedRowId) {
        setPendingScrollToRowId(storedRowId);
        return;
      }

      const storedDetails = localStorage.getItem('selectedProjectDetails');
      if (storedDetails) {
        try {
          const parsedDetails = JSON.parse(storedDetails);
          const fallbackId = parsedDetails?.Id || parsedDetails?.id_project || parsedDetails?.projectId;
          if (fallbackId) {
            setSelectedRowId(fallbackId);
            localStorage.setItem('selectedRowId', fallbackId);
            setPendingScrollToRowId(fallbackId);
          }
        } catch (error) {
          console.error('Error parsing selectedProjectDetails for scroll restoration:', error);
        }
      }
    }
  }, []);

  // Intersection Observer for infinite scroll — same approach as Contacts:
  // load the next page when the sentinel is visible inside the table scroller.
  // With root = scroll container, this fills until content can scroll, then
  // only loads more as the user scrolls (+ITEMS_PER_PAGE each time).
  useEffect(() => {
    if (loading || loadingMore || !hasMoreData) return;

    const root = tableScrollRef.current;
    const sentinel = loadingRef.current;
    if (!root || !sentinel) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries[0]?.isIntersecting) {
          loadMoreData();
        }
      },
      { root, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [loading, loadingMore, hasMoreData, currentDisplayCount]);

  const loadMoreData = () => {
    if (loadingMore || !hasMoreData) return;

    setLoadingMore(true);
    setTimeout(() => {
      setCurrentDisplayCount((prev) => {
        const filteredData = getFilteredAndSortedData();
        const newCount = Math.min(prev + ITEMS_PER_PAGE, filteredData.length);
        const hasMore = newCount < filteredData.length;
        setHasMoreData(hasMore);
        setLoadingMore(false);
        localStorage.setItem('currentDisplayCount', String(newCount));
        return newCount;
      });
    }, 300);
  };

  // Get filtered and sorted data
  const getFilteredAndSortedData = () => {
    let filtered = allProjects;

    // First filter out Material Sales projects
    filtered = filtered.filter(project => project.dv_Division_New__c !== 'Material Sales');

    // Apply column filters
    if (columnFilterValues.division && columnFilterValues.division.length > 0) {
      filtered = filtered.filter(project => columnFilterValues.division.includes(project.dv_Division_New__c));
    }
    if (columnFilterValues.scope && columnFilterValues.scope.length > 0) {
      filtered = filtered.filter(project => columnFilterValues.scope.includes(project.dv_Type__c));
    }
    if (columnFilterValues.branch && columnFilterValues.branch.length > 0) {
      filtered = filtered.filter(project => columnFilterValues.branch.includes(project.Company__c));
    }
    if (columnFilterValues.projectStatus && columnFilterValues.projectStatus.length > 0) {
      filtered = filtered.filter(project => columnFilterValues.projectStatus.includes(project.Status));
    }
    if (columnFilterValues.projectManager && columnFilterValues.projectManager.length > 0) {
      filtered = filtered.filter(project => columnFilterValues.projectManager.includes(project.manager));
    }

    // Amount range filter
    filtered = filtered.filter(project => {
      const projectAmount = parseFloat(project.RevisedRevenueBudget) || 0;
      if (amountRange.min !== null && projectAmount < amountRange.min) return false;
      if (amountRange.max !== null && projectAmount > amountRange.max) return false;
      return true;
    });

    // Billing progress range filter
    filtered = filtered.filter(project => {
      const billingProgress = parseFloat(project.BillingProgress) || 0;
      if (billingProgressRange.min !== null && billingProgress < billingProgressRange.min) return false;
      if (billingProgressRange.max !== null && billingProgress > billingProgressRange.max) return false;
      return true;
    });

    // Billing progress specific values filter
    if (columnFilterValues.billingProgress && columnFilterValues.billingProgress.length > 0) {
      filtered = filtered.filter(project =>
        columnFilterValues.billingProgress.includes(project.BillingProgress?.toString())
      );
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(project => {
        const projectDate = project.next_onsite;
        if (!projectDate) return false;
        const date = new Date(projectDate);
        if (dateRange.start && new Date(dateRange.start) > date) return false;
        if (dateRange.end && new Date(dateRange.end) < date) return false;
        return true;
      });
    }

    // Column filters
    filtered = filtered.filter(project => {
      const projectName = project.Project__r?.Name || '';
      if (columnFilters['Project__r.Name'] &&
        !projectName.toLowerCase().includes(columnFilters['Project__r.Name'].toLowerCase())) {
        return false;
      }
      const customerName1 = project.Contract_With__r?.Name || '';
      if (columnFilters['Contract_With__r.Name'] &&
        !customerName1.toLowerCase().includes(columnFilters['Contract_With__r.Name'].toLowerCase())) {
        return false;
      }
      const projectNo = project.Project__r?.Project_System_Wide_ID__c || '';
      if (columnFilters['Project__r.Project_System_Wide_ID__c'] &&
        !projectNo.toLowerCase().includes(columnFilters['Project__r.Project_System_Wide_ID__c'].toLowerCase())) {
        return false;
      }
      return true;
    });

    // Project Info filters
    filtered = filtered.filter(project => {
      if (columnFilterValues.billDate && columnFilterValues.billDate.length > 0 &&
        !columnFilterValues.billDate.includes(project.project_info?.bill_date || '')) {
        return false;
      }
      if (columnFilterValues.contract && columnFilterValues.contract.length > 0 &&
        !columnFilterValues.contract.includes(project.project_info?.contract || '')) {
        return false;
      }
      if (columnFilterValues.taxExempt && columnFilterValues.taxExempt.length > 0 &&
        !columnFilterValues.taxExempt.includes(project.project_info?.tax_exempt || '')) {
        return false;
      }
      if (columnFilterValues.certifiedPayroll && columnFilterValues.certifiedPayroll.length > 0 &&
        !columnFilterValues.certifiedPayroll.includes(project.project_info?.certified_payroll || '')) {
        return false;
      }
      if (columnFilterValues.ppBonds && columnFilterValues.ppBonds.length > 0 &&
        !columnFilterValues.ppBonds.includes(project.project_info?.p_and_p_bonds || '')) {
        return false;
      }
      if (columnFilterValues.ccip && columnFilterValues.ccip.length > 0 &&
        !columnFilterValues.ccip.includes(project.project_info?.ccip || '')) {
        return false;
      }
      if (columnFilterValues.retainage && columnFilterValues.retainage.length > 0 &&
        !columnFilterValues.retainage.includes(project.project_info?.retainage || '')) {
        return false;
      }
      if (columnFilterValues.prevailingWage && columnFilterValues.prevailingWage.length > 0 &&
        !columnFilterValues.prevailingWage.includes(project.project_info?.prevailing_wage || '')) {
        return false;
      }
      return true;
    });

    // Project Address filters
    filtered = filtered.filter(project => {
      if (columnFilterValues.street && columnFilterValues.street.length > 0 &&
        !columnFilterValues.street.includes(project.Project__r?.Street__c || '')) {
        return false;
      }
      if (columnFilterValues.city && columnFilterValues.city.length > 0 &&
        !columnFilterValues.city.includes(project.Project__r?.gmb_City__c || '')) {
        return false;
      }
      if (columnFilterValues.state && columnFilterValues.state.length > 0 &&
        !columnFilterValues.state.includes(project.Project__r?.gmb_Project_State__c || '')) {
        return false;
      }
      if (columnFilterValues.zipCode && columnFilterValues.zipCode.length > 0 &&
        !columnFilterValues.zipCode.includes(project.Project__r?.Zip_Code__c || '')) {
        return false;
      }
      return true;
    });

    // Global search term
    if (searchTerm) {
      filtered = filtered.filter(project => {
        const searchableValues = [
          project.Opportunity_System_Wide_ID__c || '',
          project.Project__r?.Name || '',
          project.dv_Type__c || '',
          project.Company__c || '',
          project.Project__r?.Project_System_Wide_ID__c || '',
          project.dv_Division_New__c || '',
          project.project_info?.bill_date || '',
          project.project_info?.contract || '',
          project.project_info?.tax_exempt || '',
          project.project_info?.certified_payroll || '',
          project.project_info?.p_and_p_bonds || '',
          project.project_info?.ccip || '',
          project.project_info?.retainage || '',
          project.project_info?.prevailing_wage || '',
          project.Project__r?.Street__c || '',
          project.Project__r?.gmb_City__c || '',
          project.Project__r?.gmb_Project_State__c || '',
          project.Project__r?.Zip_Code__c || ''
        ].map(value => value.toString().toLowerCase());

        const searchTermLower = searchTerm.toLowerCase();
        return searchableValues.some(value => value.includes(searchTermLower));
      });
    }

    // Created At date range filter
    if (createdAtRange.start || createdAtRange.end) {
      filtered = filtered.filter(project => {
        const createdAt = project.created_at;
        if (!createdAt) return false;
        const date = new Date(createdAt);
        if (createdAtRange.start && new Date(createdAtRange.start) > date) return false;
        if (createdAtRange.end && new Date(createdAtRange.end) < date) return false;
        return true;
      });
    }

    // First Onsite date range filter
    if (firstOnsiteRange.start || firstOnsiteRange.end) {
      filtered = filtered.filter(project => {
        const firstOnsite = project.first_onsite;
        if (!firstOnsite) return false;
        const date = new Date(firstOnsite);
        if (firstOnsiteRange.start && new Date(firstOnsiteRange.start) > date) return false;
        if (firstOnsiteRange.end && new Date(firstOnsiteRange.end) < date) return false;
        return true;
      });
    }

    // Most Recent Onsite date range filter
    if (mostRecentOnsiteRange.start || mostRecentOnsiteRange.end) {
      filtered = filtered.filter(project => {
        const mostRecentOnsite = project.most_recent_onsite;
        if (!mostRecentOnsite) return false;
        const date = new Date(mostRecentOnsite);
        if (mostRecentOnsiteRange.start && new Date(mostRecentOnsiteRange.start) > date) return false;
        if (mostRecentOnsiteRange.end && new Date(mostRecentOnsiteRange.end) < date) return false;
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === 'Project__r.Name') {
          aValue = a.Project__r?.Name || '';
          bValue = b.Project__r?.Name || '';
        } else if (sortConfig.key === 'Contract_With__r.Name') {
          aValue = a.Contract_With__r?.Name || '';
          bValue = b.Contract_With__r?.Name || '';
        } else if (sortConfig.key === 'Project__r.Project_System_Wide_ID__c') {
          aValue = a.Project__r?.Project_System_Wide_ID__c || '';
          bValue = b.Project__r?.Project_System_Wide_ID__c || '';
        } else if (sortConfig.key === 'Project__r.Project_Status__c') {
          aValue = a.Project__r?.Project_Status__c || '';
          bValue = b.Project__r?.Project_Status__c || '';
        } else if (sortConfig.key.includes('Project__r.')) {
          const nestedKey = sortConfig.key.split('.')[1];
          aValue = a.Project__r?.[nestedKey] || '';
          bValue = b.Project__r?.[nestedKey] || '';
        } else if (sortConfig.key.includes('project_info.')) {
          const nestedKey = sortConfig.key.split('.')[1];
          aValue = a.project_info?.[nestedKey] || '';
          bValue = b.project_info?.[nestedKey] || '';
        } else if (sortConfig.key === 'RevisedRevenueBudget') {
          aValue = parseFloat(a[sortConfig.key]) || 0;
          bValue = parseFloat(b[sortConfig.key]) || 0;
        } else if (
          sortConfig.key === 'next_onsite' ||
          sortConfig.key === 'created_at' ||
          sortConfig.key === 'first_onsite' ||
          sortConfig.key === 'most_recent_onsite'
        ) {
          aValue = new Date(a[sortConfig.key]).getTime() || 0;
          bValue = new Date(b[sortConfig.key]).getTime() || 0;
        } else if (sortConfig.key === 'BillingProgress') {
          aValue = parseFloat(a[sortConfig.key]) || 0;
          bValue = parseFloat(b[sortConfig.key]) || 0;
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        }
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      });
    }

    return filtered;
  };

  useEffect(() => {
    if (!pendingScrollToRowId || loading) return;

    const filteredData = getFilteredAndSortedData();
    if (!filteredData || filteredData.length === 0) {
      setPendingScrollToRowId(null);
      return;
    }

    const targetIndex = filteredData.findIndex(project => project.Id === pendingScrollToRowId);
    if (targetIndex === -1) {
      setPendingScrollToRowId(null);
      return;
    }

    const requiredCount = targetIndex + 1;
    if (currentDisplayCount < requiredCount) {
      setCurrentDisplayCount((prev) => {
        const targetCount = Math.min(
          Math.ceil(requiredCount / ITEMS_PER_PAGE) * ITEMS_PER_PAGE,
          filteredData.length
        );
        if (targetCount <= prev) {
          return prev;
        }
        localStorage.setItem('currentDisplayCount', targetCount.toString());
        return targetCount;
      });
      return;
    }

    const tryScroll = (attempt = 0) => {
      const rowElement = document.getElementById(`project-row-${pendingScrollToRowId}`);
      const scroller = tableScrollRef.current;
      if (rowElement && scroller) {
        const rowTop = rowElement.offsetTop;
        const target = Math.max(0, rowTop - scroller.clientHeight / 3);
        scroller.scrollTo({ top: target, behavior: 'smooth' });
        setPendingScrollToRowId(null);
        return;
      }
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPendingScrollToRowId(null);
        return;
      }
      if (attempt < 5) {
        requestAnimationFrame(() => tryScroll(attempt + 1));
      } else {
        setPendingScrollToRowId(null);
      }
    };

    requestAnimationFrame(() => tryScroll());
  }, [pendingScrollToRowId, loading, currentDisplayCount, allProjects, searchTerm, columnFilterValues, columnFilters, amountRange, billingProgressRange, dateRange, createdAtRange, firstOnsiteRange, mostRecentOnsiteRange, sortConfig]);

  // Update displayed projects based on current display count
  const displayedProjects = React.useMemo(() => {
    const filteredData = getFilteredAndSortedData();
    const sliced = filteredData.slice(0, currentDisplayCount);

    // Update hasMoreData based on current filtered data
    const hasMore = currentDisplayCount < filteredData.length;
    if (hasMoreData !== hasMore) {
      setHasMoreData(hasMore);
    }

    return sliced;
  }, [allProjects, searchTerm, columnFilterValues, columnFilters, amountRange, billingProgressRange, dateRange, createdAtRange, firstOnsiteRange, mostRecentOnsiteRange, sortConfig, currentDisplayCount, hasMoreData]);

  // Total filtered count for indicator
  const totalFilteredCount = React.useMemo(() => {
    return getFilteredAndSortedData().length;
  }, [allProjects, searchTerm, columnFilterValues, columnFilters, amountRange, billingProgressRange, dateRange, createdAtRange, firstOnsiteRange, mostRecentOnsiteRange, sortConfig]);

  // Handle sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Reset display count when sorting changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  const handleProjectSelect = async (id) => {
    if (!isReady) {
      return;
    }

    try {
      await openProjectFromOpportunity({
        authFetch,
        navigate,
        opportunityId: id,
        department: selectedDepartment,
      });
    } catch (error) {
      console.error('Error fetching project details:', error);
      setLoading(false);
    }
  };

  // Update handleRowClick to include navigation
  const handleRowClick = async (project) => {
    const department = project.dv_Division_New__c || 'Engineered Fill';
    setSelectedDepartment(department);
    setSelectedRowId(project.Id);
    localStorage.setItem('selectedDepartment', department);
    localStorage.setItem('isProjectSelected', 'true');
    localStorage.setItem('selectedRowId', project.Id);

    // Save current scroll position and display count before navigating
    localStorage.setItem('scrollPosition', window.scrollY.toString());
    localStorage.setItem('currentDisplayCount', currentDisplayCount.toString());

    window.dispatchEvent(new CustomEvent('departmentChanged', { detail: department }));

    // Fetch and store detailed project data
    await handleProjectSelect(project.Id);
  };

  // minWidth (px): scroll kicks in sooner when more columns are added; still fills width when few
  const originalColumns = [
    { key: 'Project__r.Project_System_Wide_ID__c', label: 'Project No.', filterable: false, width: 180 },
    { key: 'Project__r.Name', label: 'Project Name', filterable: false, width: 280 },
    { key: 'dv_Division_New__c', label: 'Division', filterable: true, filterKey: 'division', width: 170 },
    { key: 'dv_Type__c', label: 'Scope', filterable: true, filterKey: 'scope', width: 200 },
    { key: 'Contract_With__r.Name', label: 'Customer', filterable: false, width: 200 },
    { key: 'RevisedRevenueBudget', label: 'Amount', isRangeFilter: true, width: 150 },
    { key: 'Company__c', label: 'Branch', filterable: true, filterKey: 'branch', width: 170 },
    { key: 'manager', label: 'Project Manager', filterable: true, filterKey: 'projectManager', width: 200 },
    { key: 'Status', label: 'Status', filterable: true, filterKey: 'projectStatus', width: 150 },
    { key: 'BillingProgress', label: 'Billing Progress', filterable: true, filterKey: 'billingProgress', isCombinedFilter: true, width: 180 },
    { key: 'next_onsite', label: 'Next Onsite', isDateFilter: true, width: 160 },
    { key: 'first_onsite', label: 'First Onsite', isDateFilter: true, width: 160 },
    { key: 'most_recent_onsite', label: 'Most Recent Onsite', isDateFilter: true, width: 180 },
    { key: 'created_at', label: 'Created On', isDateFilter: true, width: 160 },
    // Project Info columns
    { key: 'project_info.bill_date', label: 'Bill Date', filterable: true, filterKey: 'billDate', width: 150 },
    { key: 'project_info.contract', label: 'Contract', filterable: true, filterKey: 'contract', width: 150 },
    { key: 'project_info.tax_exempt', label: 'Tax Exempt', filterable: true, filterKey: 'taxExempt', width: 150 },
    { key: 'project_info.certified_payroll', label: 'Certified Payroll', filterable: true, filterKey: 'certifiedPayroll', width: 180 },
    { key: 'project_info.p_and_p_bonds', label: 'P&P Bonds', filterable: true, filterKey: 'ppBonds', width: 150 },
    { key: 'project_info.ccip', label: 'CCIP', filterable: true, filterKey: 'ccip', width: 130 },
    { key: 'project_info.retainage', label: 'Retainage', filterable: true, filterKey: 'retainage', width: 150 },
    { key: 'project_info.prevailing_wage', label: 'Prevailing Wage', filterable: true, filterKey: 'prevailingWage', width: 180 },
    // Project Address columns
    { key: 'Project__r.Street__c', label: 'Street', filterable: true, filterKey: 'street', width: 220 },
    { key: 'Project__r.gmb_City__c', label: 'City', filterable: true, filterKey: 'city', width: 160 },
    { key: 'Project__r.gmb_Project_State__c', label: 'State', filterable: true, filterKey: 'state', width: 110 },
    { key: 'Project__r.Zip_Code__c', label: 'Zip Code', filterable: true, filterKey: 'zipCode', width: 130 }
  ];

  const columns = [...originalColumns].sort((a, b) => {
    const aIdx = columnOrder.indexOf(a.key);
    const bIdx = columnOrder.indexOf(b.key);
    return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
  });

  // Pinned columns stay left (in pin order); remaining visible columns follow columnOrder
  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) => visibleColumns.includes(column.key) && !pinnedVisibleKeys.includes(column.key)
  );
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  // Measure horizontal overflow so the proxy scrollbar (below the table) can be
  // sized to cover only the non-pinned area — same pattern as ModalTrackingDetails.
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      // Ignore subpixel flicker to avoid re-render loops
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // displayedProjects.length: re-measure when rows change, since a vertical
    // scrollbar appearing/disappearing changes clientWidth without resizing the
    // scroller's border-box (ResizeObserver wouldn't catch it).
  }, [tableMinWidth, pinnedColumnsWidth, loading, displayedProjects.length]);

  // Keep proxy scrollbar and table scroller in sync (either can drive)
  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  // Block horizontal wheel/trackpad panning that starts over the pinned columns;
  // vertical scrolling still works there. Touch is handled via CSS touch-action.
  // Boundary is measured from the actual pinned cell (not the logical px width)
  // so it stays correct under the global body zoom, where pointer coords and
  // layout px differ.
  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  // Pinned columns use fixed px widths so sticky left offsets stay accurate;
  // unpinned columns still grow to fill when few are selected.
  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  // Update clearStoredData function with new order
  const clearStoredData = async () => {
    // Clear all stored data
    localStorage.removeItem('projectsList');
    localStorage.removeItem('searchTerm');
    localStorage.removeItem('sortConfig');
    localStorage.removeItem('selectedRowId');
    localStorage.removeItem('selectedProjectDetails');
    localStorage.removeItem('isProjectSelected');
    localStorage.removeItem('columnFilters');
    localStorage.removeItem('amountRange');
    localStorage.removeItem('dateRange');
    localStorage.removeItem('visibleColumns');
    localStorage.removeItem('columnOrder');
    localStorage.removeItem('pinnedColumns');
    localStorage.removeItem('columnFilterValues');
    localStorage.removeItem('billingProgressRange');
    localStorage.removeItem('createdAtRange');
    localStorage.removeItem('firstOnsiteRange');
    localStorage.removeItem('mostRecentOnsiteRange');
    localStorage.removeItem('currentDisplayCount');
    localStorage.removeItem('scrollPosition');
    localStorage.removeItem('scrollToSelectedRow');

    // Reset column filters state with new order
    setColumnFilters({
      'Project__r.Name': '',
      'Contract_With__r.Name': '',
      'dv_Division_New__c': '',
      'dv_Type__c': '',
      'Company__c': '',
      'manager': '',
      'Project__r.Project_Status__c': '',
      'Billing_Progress__c': '',
      'Next_Onsite_Date__c': '',
      // Project Info filters
      'project_info.bill_date': '',
      'project_info.contract': '',
      'project_info.tax_exempt': '',
      'project_info.certified_payroll': '',
      'project_info.p_and_p_bonds': '',
      'project_info.ccip': '',
      'project_info.retainage': '',
      'project_info.prevailing_wage': '',
      // Project Address filters
      'Project__r.Street__c': '',
      'Project__r.gmb_City__c': '',
      'Project__r.gmb_Project_State__c': '',
      'Project__r.Zip_Code__c': ''
    });

    // Reset column filter values
    setColumnFilterValues({
      division: [],
      scope: [],
      branch: [],
      projectStatus: [],
      projectManager: [],
      billingProgress: [],
      // Project Info filter values
      billDate: [],
      contract: [],
      taxExempt: [],
      certifiedPayroll: [],
      ppBonds: [],
      ccip: [],
      retainage: [],
      prevailingWage: [],
      // Project Address filter values
      street: [],
      city: [],
      state: [],
      zipCode: []
    });

    // Reset range/date filters
    setAmountRange({ min: null, max: null });
    setDateRange({ start: null, end: null });
    setBillingProgressRange({ min: null, max: null });
    setCreatedAtRange({ start: null, end: null });
    setFirstOnsiteRange({ start: null, end: null });
    setMostRecentOnsiteRange({ start: null, end: null });

    // Reset display count and scroll position
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);

    // Dispatch event to notify dashboard
    window.dispatchEvent(new CustomEvent('projectDataCleared'));

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    await Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    window.location.reload();
  };

  // Handle column filter change
  const handleColumnFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Add this function to handle clearing individual filters
  const clearColumnFilter = (key) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: ''
    }));
  };

  // Get column display value for export
  const getColumnDisplayValue = (project, column) => {
    if (!project || !column) return '-';

    if (column.key === 'dv_Type__c') {
      const value = project.dv_Type__c;
      return value ? (SCOPE_LABELS[value] || value) : '-';
    }

    if (column.key === 'dv_Division_New__c') {
      const value = project.dv_Division_New__c;
      return value ? (DIVISION_LABELS[value] || value) : '-';
    }

    if (column.key === 'Status') {
      const value = project.Status;
      return value ? (STATUS_LABELS[value] || value) : '-';
    }

    if (column.key.includes('Project__r.')) {
      const nestedKey = column.key.split('.')[1];
      const value = project.Project__r?.[nestedKey];
      return value !== undefined && value !== null && value !== '' ? value : '-';
    }

    if (column.key.includes('Contract_With__r.')) {
      const nestedKey = column.key.split('.')[1];
      const value = project.Contract_With__r?.[nestedKey];
      return value !== undefined && value !== null && value !== '' ? value : '-';
    }

    if (column.key.includes('project_info.')) {
      const nestedKey = column.key.split('.')[1];
      const value = project.project_info?.[nestedKey];
      return value !== undefined && value !== null && value !== '' ? value : '-';
    }

    if (column.key === 'RevisedRevenueBudget') {
      const value = project[column.key];
      if (value === undefined || value === null || value === '') return '-';
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return '-';
      return formatCurrency(numericValue);
    }

    if (column.key === 'BillingProgress') {
      const value = project[column.key];
      if (value === undefined || value === null || value === '') return '-';
      return `${value}%`;
    }

    if (
      column.key === 'next_onsite' ||
      column.key === 'created_at' ||
      column.key === 'first_onsite' ||
      column.key === 'most_recent_onsite'
    ) {
      const value = project[column.key];
      return value ? formatUSDate(value) : '-';
    }

    const directValue = project[column.key];
    if (directValue === undefined || directValue === null || directValue === '') return '-';

    return directValue;
  };

  // Handle Excel export
  const handleExportToExcel = async () => {
    if (isExporting) return;

    const exportColumns = columns.filter(column => visibleColumns.includes(column.key));
    if (exportColumns.length === 0) {
      console.warn('No columns selected for export');
      return;
    }

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        console.error('XLSX library not available');
        return;
      }

      const headers = exportColumns.map(column => column.label);
      const exportData = getFilteredAndSortedData();
      const rows = exportData.map(project =>
        exportColumns.map(column => getColumnDisplayValue(project, column))
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Projects');
      XLSX.writeFile(workbook, 'projects.xlsx');
    } catch (error) {
      console.error('Failed to export projects:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleColumnFilter = (columnName, event) => {
    toggleFilterPopup(columnName, event);
  };

  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues(prev => ({
      ...prev,
      [columnName]: prev[columnName].includes(value)
        ? prev[columnName].filter(v => v !== value)
        : [...prev[columnName], value]
    }));
    // Reset display count when filter changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  // Update the getUniqueValues function to exclude Material Sales from filters
  const getUniqueValues = (data, key, currentFilters) => {
    // First filter out Material Sales from the data
    const dataWithoutMaterialSales = data.filter(project =>
      project.dv_Division_New__c !== 'Material Sales'
    );

    // Don't filter based on current filter selections
    // This ensures we always show all options regardless of current selections
    const filteredData = dataWithoutMaterialSales;

    // Debug logging for project_info fields
    if (key.includes('project_info.')) {
      console.log(`Getting unique values for ${key}:`, filteredData.map(item => item.project_info?.[key.split('.')[1]]).filter(Boolean));
    }

    // Then get unique values from the filtered data
    if (key === 'dv_Type__c') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(Boolean)
        .map(value => ({
          value,
          label: SCOPE_LABELS[value] || value
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (key === 'dv_Division_New__c') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-' && value !== 'Material Sales')
        .map(value => ({
          value,
          label: DIVISION_LABELS[value] || value
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    // Update Project Status handling to use dynamic values
    if (key === 'Status') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-')
        .map(value => ({
          value,
          label: STATUS_LABELS[value] || value
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    // Handle project manager values
    if (key === 'manager') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-')
        .sort();
    }

    // Handle BillingProgress values
    if (key === 'BillingProgress') {
      return [...new Set(filteredData.map(item => item[key]?.toString()))]
        .filter(value => value && value !== '-')
        .sort((a, b) => parseFloat(a) - parseFloat(b)); // Sort numerically
    }

    return [...new Set(filteredData.map(item => {
      if (key.includes('Project__r.')) {
        const nestedKey = key.split('.')[1];
        return item.Project__r?.[nestedKey];
      }
      if (key.includes('project_info.')) {
        const nestedKey = key.split('.')[1];
        return item.project_info?.[nestedKey];
      }
      return item[key];
    }))]
      .filter(Boolean)
      .sort();
  };

  // Add this function in ManageProject component
  const handleToggleColumn = (columnKey) => {
    const isHiding = visibleColumns.includes(columnKey);
    const newColumns = isHiding
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    setVisibleColumns(newColumns);
    saveVisibleColumns(newColumns);

    if (isHiding && pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }
  };

  const handleTogglePin = (columnKey) => {
    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }

    // Ensure column is visible when pinning
    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }

    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;

    setColumnOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    saveColumnOrder();
  };

  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);

  const handleHeaderDragStart = (e, columnKey) => {
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey) return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    // Only reorder within the same pin group so sticky left columns stay coherent
    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;
    if (wasPinned) {
      savePinnedColumns();
    } else {
      saveColumnOrder();
    }
  };

  // Update the button click handler to use the button's bounding rect
  const handleColumnSelectorClick = () => {
    setIsColumnSelectorOpen((prev) => {
      const next = !prev;
      if (next && columnButtonRef.current) {
        const rect = columnButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 192; // w-48 in px
        // Use fixed positioning - getBoundingClientRect() returns viewport coordinates
        // which work correctly with position: fixed
        let left = rect.left;
        let top = rect.bottom + 4; // 4px gap below button

        // If dropdown would overflow right, align right edge with button's right edge
        if (left + dropdownWidth > window.innerWidth - 8) {
          left = rect.right - dropdownWidth;
        }
        if (left < 8) {
          left = 8;
        }
        setColumnSelectorPosition({ top, left });
      }
      return next;
    });
  };

  // Update dropdown position when it opens
  useEffect(() => {
    if (isColumnSelectorOpen && columnButtonRef.current) {
      const updatePosition = () => {
        if (columnButtonRef.current) {
          const rect = columnButtonRef.current.getBoundingClientRect();
          const dropdownWidth = 192; // w-48 in px
          let left = rect.left;
          let top = rect.bottom + 4; // 4px gap below button

          // If dropdown would overflow right, align right edge with button's right edge
          if (left + dropdownWidth > window.innerWidth - 8) {
            left = rect.right - dropdownWidth;
          }
          if (left < 8) {
            left = 8;
          }
          setColumnSelectorPosition({ top, left });
        }
      };

      // Update position immediately
      updatePosition();

      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isColumnSelectorOpen]);

  // Click outside for columns dropdown
  useEffect(() => {
    if (!isColumnSelectorOpen) return;
    function handleClickOutside(event) {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSelectorOpen]);

  // Click outside for filter dropdowns
  // UX: Keep filter dropdowns open while user scrolls/taps elsewhere.
  // They should close only via `Clear`/`Apply` or by tapping the same filter again.

  // Persist columnFilters to localStorage
  useEffect(() => {
    localStorage.setItem('columnFilters', JSON.stringify(columnFilters));
  }, [columnFilters]);

  // Persist columnFilterValues to localStorage
  useEffect(() => {
    localStorage.setItem('columnFilterValues', JSON.stringify(columnFilterValues));
  }, [columnFilterValues]);

  // Persist amountRange to localStorage
  useEffect(() => {
    localStorage.setItem('amountRange', JSON.stringify(amountRange));
  }, [amountRange]);

  // Persist dateRange to localStorage
  useEffect(() => {
    localStorage.setItem('dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  // Persist billingProgressRange to localStorage
  useEffect(() => {
    localStorage.setItem('billingProgressRange', JSON.stringify(billingProgressRange));
  }, [billingProgressRange]);

  // Persist createdAtRange to localStorage
  useEffect(() => {
    localStorage.setItem('createdAtRange', JSON.stringify(createdAtRange));
  }, [createdAtRange]);

  useEffect(() => {
    localStorage.setItem('firstOnsiteRange', JSON.stringify(firstOnsiteRange));
  }, [firstOnsiteRange]);

  useEffect(() => {
    localStorage.setItem('mostRecentOnsiteRange', JSON.stringify(mostRecentOnsiteRange));
  }, [mostRecentOnsiteRange]);

  // Persist sortConfig to localStorage
  useEffect(() => {
    localStorage.setItem('sortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);


  // Persist searchTerm to localStorage
  useEffect(() => {
    localStorage.setItem('searchTerm', searchTerm);
  }, [searchTerm]);

  // Persist selectedRowId to localStorage
  useEffect(() => {
    localStorage.setItem('selectedRowId', selectedRowId);
  }, [selectedRowId]);

  // Persist selectedDepartment to localStorage
  useEffect(() => {
    localStorage.setItem('selectedDepartment', selectedDepartment);
  }, [selectedDepartment]);

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              id="projectSearch"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                setHasMoreData(true);
              }}
            />
          </form>
          <div className="flex-grow max-md:hidden"></div>

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={columnSelectorRef}
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                position={columnSelectorPosition}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || displayedProjects.length === 0 || isExporting}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${loading || displayedProjects.length === 0 || isExporting ? 'cursor-not-allowed opacity-60' : ''
                }`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={clearStoredData} tooltip="Reset" />
          </div>
        </div>
      </div>

      {/* Table fills remaining card height; scrollbars stay on screen */}
      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
          <table
            className="projects-data-table"
            style={{
              width: `max(100%, ${tableMinWidth}px)`,
              minWidth: `${tableMinWidth}px`,
            }}
          >
              <colgroup>
                {visibleTableColumns.map((column) => {
                  const isPinned = pinnedVisibleKeys.includes(column.key);
                  return (
                    <col
                      key={column.key}
                      style={
                        isPinned
                          ? {
                              width: `${column.width}px`,
                              minWidth: `${column.width}px`,
                            }
                          : {
                              width: `${(column.width / tableMinWidth) * 100}%`,
                              minWidth: `${column.width}px`,
                            }
                      }
                    />
                  );
                })}
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  {visibleTableColumns.map((column) => {
                    const isPinned = pinnedVisibleKeys.includes(column.key);
                    return (
                    <th
                      key={column.key}
                      style={getColumnStyle(column, { isHeader: true })}
                      className={`group/header px-4 py-3 max-md:whitespace-normal cursor-grab active:cursor-grabbing transition-colors hover:bg-gray-100 ${
                        isPinned ? 'projects-col-pinned' : ''
                      } ${column.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleHeaderDragStart(e, column.key)}
                      onDragOver={(e) => handleHeaderDragOver(e, column.key)}
                      onDragEnd={handleHeaderDragEnd}
                    >
                      <div className="flex flex-col gap-2">
                        <div
                          className="flex items-center justify-center text-sm font-medium text-gray-500 cursor-pointer whitespace-nowrap"
                          onClick={() => requestSort(column.key)}
                        >
                          <span className="truncate" title={column.label}>{column.label}</span>
                          <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                            <SortIcon direction={sortIconDirection(sortConfig, column.key)} />
                          </span>
                          <button
                            type="button"
                            data-pin-toggle
                            title={isPinned ? 'Unpin column' : 'Pin column to left'}
                            aria-label={isPinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(column.key);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                              isPinned
                                ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                                : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                            }`}
                          >
                            <span className="inline-flex">
                              {isPinned ? (
                                <MdPushPin className="h-3.5 w-3.5" />
                              ) : (
                                <MdOutlinePushPin className="h-3.5 w-3.5" />
                              )}
                            </span>
                          </button>
                        </div>
                        <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                          {/* Only render filter controls for visible columns */}
                          {visibleColumns.includes(column.key) && (
                            <>
                              {column.filterable && column.isCombinedFilter ? (
                                <div className="relative" ref={activeFilterColumn === 'billingProgress' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'billingProgress')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('billingProgress', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${columnFilterValues[column.filterKey]?.length > 0 || billingProgressRange.min !== null || billingProgressRange.max !== null ? 'text-red-600 border-red-500' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span>
                                      {columnFilterValues[column.filterKey]?.length > 0
                                        ? `${columnFilterValues[column.filterKey].length} selected`
                                        : (billingProgressRange.min !== null || billingProgressRange.max !== null)
                                          ? 'Filtered'
                                          : 'Filter'}
                                    </span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === 'billingProgress' && renderFilterPopup(
                                    <BillingProgressFilterDropdown
                                      isOpen={activeFilterColumn === 'billingProgress'}
                                      options={getUniqueValues(allProjects, column.key, {})}
                                      selectedValues={columnFilterValues[column.filterKey] || []}
                                      onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                      onApply={setBillingProgressRange}
                                      onClose={closeFilterPopup}
                                      currentRange={billingProgressRange}
                                    />
                                  )}
                                </div>
                              ) : column.isRangeFilter ? (
                                <div className="relative" ref={activeFilterColumn === 'amount' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'amount')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('amount', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${amountRange.min !== null || amountRange.max !== null ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span>Filter</span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === 'amount' && renderFilterPopup(
                                    <RangeFilterDropdown
                                      isOpen={activeFilterColumn === 'amount'}
                                      onApply={setAmountRange}
                                      onClose={closeFilterPopup}
                                      currentRange={amountRange}
                                    />
                                  )}
                                </div>
                              ) : column.filterable ? (
                                <div className="relative" ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}>
                                  <button
                                    onClick={(e) => toggleColumnFilter(column.filterKey, e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${columnFilterValues[column.filterKey]?.length > 0 ? 'text-red-600 border-red-500' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span>
                                      {columnFilterValues[column.filterKey]?.length > 0
                                        ? `${columnFilterValues[column.filterKey].length} selected`
                                        : 'Filter'}
                                    </span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === column.filterKey && renderFilterPopup(
                                    <ColumnFilterDropdown
                                      isOpen={activeFilterColumn === column.filterKey}
                                      options={getUniqueValues(allProjects, column.key, {})}
                                      selectedValues={columnFilterValues[column.filterKey] || []}
                                      onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                      onClose={closeFilterPopup}
                                      maxHeight={isMobile ? '50vh' : '60vh'}
                                    />
                                  )}
                                </div>
                              ) : column.isDateFilter && column.key === 'next_onsite' ? (
                                <div className="relative" ref={activeFilterColumn === 'next_onsite' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'next_onsite')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('next_onsite', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${dateRange.start || dateRange.end ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span className="truncate">
                                      {dateRange.start || dateRange.end ? (
                                        <span className="text-xs">
                                          {dateRange.start ? formatDateForDisplay(dateRange.start) : 'Any'}
                                          {' - '}
                                          {dateRange.end ? formatDateForDisplay(dateRange.end) : 'Any'}
                                        </span>
                                      ) : (
                                        'Filter'
                                      )}
                                    </span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'next_onsite' && renderFilterPopup(
                                    <DateRangeFilterDropdown
                                      isOpen={activeFilterColumn === 'next_onsite'}
                                      onApply={setDateRange}
                                      onClose={closeFilterPopup}
                                      currentRange={dateRange}
                                    />
                                  )}
                                </div>
                              ) : column.isDateFilter && column.key === 'created_at' ? (
                                <div className="relative" ref={activeFilterColumn === 'created_at' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'created_at')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('created_at', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${createdAtRange.start || createdAtRange.end ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span className="truncate">
                                      {createdAtRange.start || createdAtRange.end ? (
                                        <span className="text-xs">
                                          {createdAtRange.start ? formatDateForDisplay(createdAtRange.start) : 'Any'}
                                          {' - '}
                                          {createdAtRange.end ? formatDateForDisplay(createdAtRange.end) : 'Any'}
                                        </span>
                                      ) : (
                                        'Filter'
                                      )}
                                    </span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'created_at' && renderFilterPopup(
                                    <DateRangeFilterDropdown
                                      isOpen={activeFilterColumn === 'created_at'}
                                      onApply={setCreatedAtRange}
                                      onClose={closeFilterPopup}
                                      currentRange={createdAtRange}
                                    />
                                  )}
                                </div>
                              ) : column.isDateFilter && column.key === 'first_onsite' ? (
                                <div className="relative" ref={activeFilterColumn === 'first_onsite' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'first_onsite')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('first_onsite', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${firstOnsiteRange.start || firstOnsiteRange.end ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span className="truncate">
                                      {firstOnsiteRange.start || firstOnsiteRange.end ? (
                                        <span className="text-xs">
                                          {firstOnsiteRange.start ? formatDateForDisplay(firstOnsiteRange.start) : 'Any'}
                                          {' - '}
                                          {firstOnsiteRange.end ? formatDateForDisplay(firstOnsiteRange.end) : 'Any'}
                                        </span>
                                      ) : (
                                        'Filter'
                                      )}
                                    </span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'first_onsite' && renderFilterPopup(
                                    <DateRangeFilterDropdown
                                      isOpen={activeFilterColumn === 'first_onsite'}
                                      onApply={setFirstOnsiteRange}
                                      onClose={closeFilterPopup}
                                      currentRange={firstOnsiteRange}
                                    />
                                  )}
                                </div>
                              ) : column.isDateFilter && column.key === 'most_recent_onsite' ? (
                                <div className="relative" ref={activeFilterColumn === 'most_recent_onsite' ? filterDropdownRef : null} style={getFilterWrapperStyle(activeFilterColumn === 'most_recent_onsite')}>
                                  <button
                                    onClick={(e) => toggleFilterPopup('most_recent_onsite', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${mostRecentOnsiteRange.start || mostRecentOnsiteRange.end ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span className="truncate">
                                      {mostRecentOnsiteRange.start || mostRecentOnsiteRange.end ? (
                                        <span className="text-xs">
                                          {mostRecentOnsiteRange.start ? formatDateForDisplay(mostRecentOnsiteRange.start) : 'Any'}
                                          {' - '}
                                          {mostRecentOnsiteRange.end ? formatDateForDisplay(mostRecentOnsiteRange.end) : 'Any'}
                                        </span>
                                      ) : (
                                        'Filter'
                                      )}
                                    </span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'most_recent_onsite' && renderFilterPopup(
                                    <DateRangeFilterDropdown
                                      isOpen={activeFilterColumn === 'most_recent_onsite'}
                                      onApply={setMostRecentOnsiteRange}
                                      onClose={closeFilterPopup}
                                      currentRange={mostRecentOnsiteRange}
                                    />
                                  )}
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  placeholder="Search"
                                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                                  value={columnFilters[column.key] || ''}
                                  onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={visibleTableColumns.length} className="p-0">
                      <div
                        className="projects-table-loading-inline"
                        style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                        role="status"
                        aria-live="polite"
                      >
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                        <span className="ml-2">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : fetchError ? (
                  <tr>
                    <td colSpan={visibleTableColumns.length} className="text-center py-4 text-red-700">
                      Failed to load projects: {fetchError}
                    </td>
                  </tr>
                ) : displayedProjects.length === 0 ? (
                  <tr>
                    <td colSpan={visibleTableColumns.length} className="text-center py-4">No projects found</td>
                  </tr>
                ) : (
                  displayedProjects.map((project, index) => (
                    <tr
                      id={`project-row-${project.Id}`}
                      key={project.Id || index}
                      className={`hover:bg-gray-50 cursor-pointer ${project.Id === selectedRowId ? 'bg-red-50' : ''
                        }`}
                      onClick={() => handleRowClick(project)}
                    >
                      {visibleTableColumns.map((column) => {
                        const isPinned = pinnedVisibleKeys.includes(column.key);
                        return (
                          <TooltipCell
                            key={column.key}
                            content={
                              column.key === 'dv_Type__c'
                                ? SCOPE_LABELS[project.dv_Type__c] || project.dv_Type__c || '-'
                                : column.key === 'dv_Division_New__c'
                                  ? DIVISION_LABELS[project.dv_Division_New__c] || project.dv_Division_New__c || '-'
                                  : column.key === 'Status'
                                    ? STATUS_LABELS[project.Status] || project.Status || '-'
                                    : column.key.includes('Project__r.')
                                      ? project.Project__r?.[column.key.split('.')[1]] || '-'
                                      : column.key.includes('Contract_With__r.')
                                        ? project.Contract_With__r?.[column.key.split('.')[1]] || '-'
                                        : column.key.includes('project_info.')
                                          ? project.project_info?.[column.key.split('.')[1]] || '-'
                                          : column.key === 'RevisedRevenueBudget'
                                            ? project[column.key]
                                              ? new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0
                                              }).format(parseFloat(project[column.key]))
                                              : '-'
                                            : column.key === 'BillingProgress'
                                              ? project[column.key] ? `${project[column.key]}%` : '-'
                                              : column.key === 'next_onsite' || column.key === 'created_at' || column.key === 'first_onsite' || column.key === 'most_recent_onsite'
                                                ? (project[column.key] ? formatUSDate(project[column.key]) : '-')
                                                : project[column.key] || '-'
                            }
                            className={`text-gray-900 max-md:whitespace-normal break-words${
                              isPinned ? ' projects-col-pinned' : ''
                            }${column.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`}
                            align="left"
                            style={getColumnStyle(column)}
                          />
                        );
                      })}
                    </tr>
                  ))
                )}
                {/* Infinite scroll loading indicator */}
                {!loading && displayedProjects.length > 0 && hasMoreData && (
                  <tr ref={loadingRef}>
                    <td colSpan={visibleTableColumns.length} className="p-0">
                      <div
                        className="projects-table-loading-inline"
                        style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                      >
                        {loadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700" />
                            <span className="ml-2">Loading more...</span>
                          </>
                        ) : (
                          <span className="text-gray-500">Scroll down to load more</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {/* End of data indicator */}
                {!loading && displayedProjects.length > 0 && !hasMoreData && (
                  <tr>
                    <td colSpan={visibleTableColumns.length} className="p-0">
                      <div
                        className="projects-table-loading-inline"
                        style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                      >
                        <span className="text-gray-500">No more data to load</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
          </table>
        </div>
      {/* Proxy horizontal scrollbar — starts after the pinned columns (Tracking Details pattern).
       * Width is derived from the scroller's measured clientWidth (excludes the vertical
       * scrollbar gutter) so the proxy's travel maps 1:1 to the table's scrollLeft. */}
      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}
      <RowsIndicator loadedCount={displayedProjects.length} totalCount={totalFilteredCount} />
    </div>
  );
} 